import { useMemo, CSSProperties, useCallback } from 'react'
import { renderToString } from 'react-dom/server'
import { DpcRecord } from 'interface/dpc/types'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'

import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from 'echarts/components'
import { Typography } from '@mui/material'
import { Circle as CircleIcon } from '@mui/icons-material'
import { distinct } from 'shared/util'
import { SummaryFieldTypes } from 'features/dpcResult/types'

const toolTipMargin = { x: 20, y: 10 }

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  CanvasRenderer,
])

const colors = [
  '#005eaa',
  '#e6b600',
  '#c12e34',
  '#2b821d',
  '#339ca8',
  '#32a487',
  '#971f6f',
  '#444444',
]

type DpcSeries = {
  title: string
  stack: SummaryFieldTypes
  color?: string
  val: (record: DpcRecord) => number | null | undefined
}
const serieses: { [seriesName: string]: DpcSeries } = {
  surgery_s01_case: {
    stack: 'surgery',
    val: (record) => record.surgery?.s01?.cases,
    color: colors[0],
    title: '手術(1)',
  },
  surgery_s02_case: {
    stack: 'surgery',
    val: (record) => record.surgery?.s02?.cases,
    color: colors[1],
    title: '手術(2)',
  },
  surgery_s03_case: {
    stack: 'surgery',
    val: (record) => record.surgery?.s03?.cases,
    color: colors[2],
    title: '手術(3)',
  },
  surgery_s04_case: {
    stack: 'surgery',
    val: (record) => record.surgery?.s04?.cases,
    color: colors[3],
    title: '手術(4)',
  },
  surgery_s05_case: {
    stack: 'surgery',
    val: (record) => record.surgery?.s05?.cases,
    color: colors[4],
    title: '手術(5)',
  },
  surgery_s06_case: {
    stack: 'surgery',
    val: (record) => record.surgery?.s06?.cases,
    color: colors[5],
    title: '手術(6)',
  },
  surgery_s97_case: {
    stack: 'surgery',
    val: (record) => record.surgery?.s97?.cases,
    color: colors[6],
    title: 'その他手術',
  },
  surgery_s99_case: {
    stack: 'surgery',
    val: (record) => record.surgery?.s99?.cases,
    color: colors[7],
    title: '手術なし',
  },

  treatment1_s00_case: {
    stack: 'treatment1',
    val: (record) => record.treatment1?.s00?.cases,
    color: colors[0],
    title: '手術なし／処置なし',
  },
  treatment1_s01_case: {
    stack: 'treatment1',
    val: (record) => record.treatment1?.s01?.cases,
    color: colors[1],
    title: '手術なし／処置あり',
  },
  treatment1_s10_case: {
    stack: 'treatment1',
    val: (record) => record.treatment1?.s10?.cases,
    color: colors[2],
    title: '手術あり／処置なし',
  },
  treatment1_s11_case: {
    stack: 'treatment1',
    val: (record) => record.treatment1?.s11?.cases,
    color: colors[3],
    title: '手術あり／処置あり',
  },

  treatment2_s00_case: {
    stack: 'treatment2',
    val: (record) => record.treatment2?.s00?.cases,
    color: colors[0],
    title: '手術なし／処置なし',
  },
  treatment2_s01_case: {
    stack: 'treatment2',
    val: (record) => record.treatment2?.s01?.cases,
    color: colors[1],
    title: '手術なし／処置あり',
  },
  treatment2_s10_case: {
    stack: 'treatment2',
    val: (record) => record.treatment2?.s10?.cases,
    color: colors[2],
    title: '手術あり／処置なし',
  },
  treatment2_s11_case: {
    stack: 'treatment2',
    val: (record) => record.treatment2?.s11?.cases,
    color: colors[3],
    title: '手術あり／処置あり',
  }
}

type DpcGraphToolTipProps = {
  year: number
  data: {
    title: string
    color?: string
    value: number
    isCurrent: boolean
  }[]
}
const DpcGraphToolTip = ({
  year,
  data
}: DpcGraphToolTipProps) => {
  if (data.length === 0) return null
  return (
    <div>
      <Typography component="div" variant='body2'>{year} 年</Typography>
      {data.map((series, j) => (
        <div key={`series-${j}`} style={{ display: "flex", alignItems: "center", marginTop: 4, width: "100%" }}>
          <CircleIcon style={{ color: series.color, height: "1rem", width: "1rem" }}/>
          <Typography
            component="div"
            variant='body2'
            style={{
              marginLeft: 8,
              flexGrow: 1,
              fontWeight: series.isCurrent ? 'bold' : undefined
            }}
          >
            {series.title}
          </Typography>
          <Typography
            component="div"
            variant='body2'
            style={{
              marginLeft: 8,
              fontWeight: series.isCurrent ? 'bold' : undefined
            }}
          >
            {series.value} 件
          </Typography>
        </div>
      ))}
    </div>
  )
}

type DpcNendoGraphProps = {
  list: DpcRecord[]
  width?: number | 'auto'
  height?: number | 'auto'
  style?: CSSProperties
}
export const DpcNendoGraph = ({
  list,
  width,
  height,
  style
}: DpcNendoGraphProps) => {
  const validSerieses = useMemo(
    () => Object.values(serieses)
    .filter(
      series => list.some(record => series.val(record))
    )
    .map(
      series => ({
        ...series,
        data: list.map(record => series.val(record) ?? null)
      })
    ),
    [list]
  )
  const years = useMemo(
    () => distinct(list.map(record => record.year).reverse()),
    [list]
  )
  const tooltipFormatter = useCallback(
    (params: {
      seriesIndex?: number
      dataIndex?: number
    }) => {
      //console.log(`series: ${params.seriesIndex}, data: ${params.dataIndex}`, params)
      const { dataIndex, seriesIndex } = params
      if (dataIndex === undefined || seriesIndex === undefined) return null

      const data = validSerieses.flatMap((series, i) => {
        const value = series.data[dataIndex]
        return value ? {
          color: series.color,
          title: series.title,
          value,
          isCurrent: i === seriesIndex
        } : []
      })
      return renderToString(
        <DpcGraphToolTip year={years[dataIndex]} data={data}/>
      )
    },
    [years, validSerieses]
  )
  const tooltipPosition = useCallback(
    (pos: number[], params: unknown, dom: unknown, rect: unknown, size: { viewSize: number[], contentSize: number[] }) => {
      const left = Math.max(toolTipMargin.y, Math.min(size.viewSize[0] - (size.contentSize[0] + toolTipMargin.y), 
        (pos[0] < size.viewSize[0] / 2) ? (pos[0] + toolTipMargin.x) : (pos[0] - (size.contentSize[0] + toolTipMargin.x))
      ))
      const top = Math.max(toolTipMargin.y, Math.min(size.viewSize[1] - (size.contentSize[1] + toolTipMargin.y), 
        (pos[1] < size.viewSize[1] / 2) ? (pos[1] + toolTipMargin.y) : (pos[1] - (size.contentSize[1] + toolTipMargin.y))
      ))
      return { top, left }
    },
    []
  )
  return (
    <ReactEChartsCore
      echarts={echarts}
      style={style}
      notMerge={true}
      option={{
        //animation: false,
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'shadow'
          },
          formatter: tooltipFormatter,
          position: tooltipPosition
        },
        legend: {
          type: 'scroll',
          orient: 'horizontal',
          left: 10,
          top: 10
        },
        grid: {
          top: 50,
          left: 10,
          right: 10,
          bottom: 10,
          outerBoundsMode: 'auto',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: years,
          inverse: true,
          axisLabel: {
            formatter: '{value} 年'
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: '{value} 件'
          }
        },
        series: validSerieses.map(series => ({
          name: series.title,
          type: 'bar',
          stack: series.stack,
          stackOrder: 'seriesDesc',
          label: {
            show: true,
            position: 'inside',
            formatter: '{c} 件'
          },
          emphasis: { focus: 'series' },
          itemStyle: { color: series.color },
          data: series.data
        })),
        textStyle: {
          fontFamily: 'Noto Sans JP,sans-serif',
        },
      }}
      opts={{
        renderer: 'canvas',
        width: width,
        height: height,
      }}
      theme="insite"
    />
  )
}
